import classnames from 'classnames'

import type { ArticlePreviewCardProps } from '~/components/atoms/ArticlePreviewCard/ArticlePreviewCard'
import ArticlePreviewCard from '~/components/atoms/ArticlePreviewCard/ArticlePreviewCard'
import Container from '~/components/atoms/Container/Container'
import DynamicSlider from '~/components/atoms/DynamicSlider'
import Fixture from '~/components/atoms/Fixture/Fixture'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { ComponentTheme } from '~/components/themes'
import type { TSearchAnalystEventProps } from '~/services/elastic/analyst'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'

import styles from './CalloutVideoSlider.module.css'

export interface CalloutVideoSliderProps {
  articles: ArticlePreviewCardProps[]
  theme?: ComponentTheme
  testID?: string
  title?: string
  summary?: string
  sectionId?: string
  noPaddingBottom?: boolean
  searchInsightsEvent?: TSearchAnalystEventProps
  hideArrows?: boolean
  hideAuthors?: boolean
  testAttr?: string
}

export default function CalloutVideoSlider({
  articles,
  theme = ComponentTheme.primary,
  testID,
  title,
  summary,
  sectionId,
  noPaddingBottom,
  hideArrows = true,
  searchInsightsEvent,
  hideAuthors = false,
  testAttr = 'related-articles',
}: CalloutVideoSliderProps) {
  const isMobile = useMediaQuery(`(max-width: ${bp.bpLarge})`)

  return (
    <div
      className={classnames(styles.base)}
      data-testid={testID}
      data-test={testAttr}
    >
      <Fixture
        theme={theme}
        verticalPadding={theme !== ComponentTheme.magazine}
        noPaddingBottom={!isMobile ? articles.length <= 3 : noPaddingBottom}
      >
        <div style={{ overflow: 'hidden' }}>
          <div
            className={classnames(styles.wrap, {
              // Exactly 3 cards sit in a 3.4-slot track on desktop, so centre them
              [styles.centred]: !isMobile && articles?.length === 3,
            })}
          >
            <Container
              cssClass={classnames(styles[theme], {
                [styles.sliderContainer]: !isMobile
                  ? articles?.length > 3
                  : true,
              })}
            >
              <div className={styles.content}>
                {(title || summary) && (
                  <div className={styles.introContainer}>
                    {title && (
                      <Text
                        className={styles.title}
                        size={TextSizes.primaryTitleLrg}
                      >
                        <div dangerouslySetInnerHTML={{ __html: title }}></div>
                      </Text>
                    )}
                    {summary && (
                      <Text className={styles.summary} size={TextSizes.bodyMed}>
                        <div
                          dangerouslySetInnerHTML={{ __html: summary }}
                        ></div>
                      </Text>
                    )}
                  </div>
                )}
              </div>
              <DynamicSlider
                slidesPerView={articles?.length >= 3 ? 1.4 : 3}
                slidesPerViewMed={2.4}
                slidesPerViewLrg={articles?.length >= 3 ? 3.4 : 3}
                overflowVisible={true}
                sectionId={sectionId}
                hideTitle={true}
                hideArrows={hideArrows}
                hideArrowsMobile={true}
                useScrollbar
                spacer={false}
                wrapperClass={styles.sliderWrapperClass}
                initiateInitialSlide={true}
              >
                {articles?.map((item: ArticlePreviewCardProps, i: number) => {
                  return (
                    <ArticlePreviewCard
                      {...item}
                      title={`${item.title}`}
                      category="Guide"
                      modules={item.modules}
                      imageType="landscape"
                      summaryLimit={200}
                      showReadMore={theme === ComponentTheme.magazine}
                      hideSummary={theme === ComponentTheme.magazine}
                      variant={
                        theme === ComponentTheme.magazine
                          ? 'magazine'
                          : 'medium'
                      }
                      theme={theme}
                      leadImage={item.leadImage}
                      hideAuthor={hideAuthors}
                      key={`related-articles-${i}`}
                      titleTag="h3"
                      sizes="(max-width: 559px) 100vw, (max-width: 1079px) 33vw, 25vw"
                      searchInsightsEvent={searchInsightsEvent}
                      testAttr={`${testAttr}-item`}
                      noPrefetch={true}
                      openWatchModal
                    />
                  )
                })}
              </DynamicSlider>
            </Container>
          </div>
        </div>
      </Fixture>
    </div>
  )
}
